import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import api, { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  RefreshCw, AlertTriangle, LayoutDashboard, Megaphone, Target, Zap, Sparkles, Wallet, Download, Send,
} from 'lucide-react';
import HelpTooltip from '@/components/help/HelpTooltip';
import { usePaletaDeGraficas } from '@/lib/paletaDeGraficas';
import FiltrosDelTablero, { mesActual } from '@/components/FiltrosDelTablero';
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  RadialBarChart, RadialBar, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, PolarAngleAxis, Legend,
} from 'recharts';

// La paleta ya no vive aquí: la sirve `usePaletaDeGraficas()`, que la lee de
// las variables de CSS para que las gráficas sigan el modo claro u oscuro.
// Recharts pinta atributos SVG y el CSS no los alcanza; hay que resolverlos
// en JavaScript. Ver lib/paletaDeGraficas.js.
const WEEKDAY_LABELS_DOW = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb']; // Postgres EXTRACT(dow) = 0-6, Sun-Sat

const TABS = [
  { key: 'resumen', label: 'Resumen', icon: LayoutDashboard },
  { key: 'marketing', label: 'Marketing', icon: Megaphone },
  { key: 'ventas', label: 'Ventas', icon: Target },
  { key: 'automatizacion', label: 'Automatización', icon: Zap },
  { key: 'ia', label: 'IA Insights', icon: Sparkles },
  { key: 'caja', label: 'Caja', icon: Wallet },
];

function money(v) {
  return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0 }).format(v || 0);
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function KpiCard({ label, value, sub, tone }) {
  return (
    <div className="bg-white border border-[#E2E8F0] rounded-xl p-4 hover:-translate-y-0.5 hover:border-[#CBD5E1] transition-all">
      <p className="text-xs uppercase tracking-widest text-[#64748B] font-semibold">{label}</p>
      <p className={`text-2xl sm:text-3xl font-bold tracking-tight mt-1 ${tone || 'text-[#0F172A]'}`}>{value}</p>
      {sub && <p className="text-xs text-[#94A3B8] mt-1">{sub}</p>}
    </div>
  );
}

function Panel({ title, help, children, testId }) {
  return (
    <div className="bg-white border border-[#E2E8F0] rounded-xl p-4 sm:p-5" data-testid={testId}>
      <h3 className="text-sm font-bold text-[#0F172A] flex items-center gap-2 mb-3">
        {title} {help && <HelpTooltip text={help} />}
      </h3>
      {children}
    </div>
  );
}

function Heatmap({ data }) {
  // data: [{dow, hour, count}], dow 0-6 (Postgres: 0=Sunday)
  const grid = {};
  let max = 0;
  data.forEach(d => {
    grid[`${d.dow}-${d.hour}`] = d.count;
    if (d.count > max) max = d.count;
  });
  const orderedDows = [1, 2, 3, 4, 5, 6, 0]; // Mon..Sun for display
  return (
    <div className="overflow-x-auto">
      <table className="border-collapse text-[10px]">
        <thead>
          <tr>
            <th className="w-10"></th>
            {Array.from({ length: 24 }, (_, h) => <th key={h} className="px-0.5 font-normal text-[#94A3B8]">{h}</th>)}
          </tr>
        </thead>
        <tbody>
          {orderedDows.map(dow => (
            <tr key={dow}>
              <td className="pr-2 text-[#64748B] text-right font-medium">{WEEKDAY_LABELS_DOW[dow]}</td>
              {Array.from({ length: 24 }, (_, h) => {
                const count = grid[`${dow}-${h}`] || 0;
                const intensity = max > 0 ? count / max : 0;
                return (
                  <td key={h} className="p-0">
                    <div
                      className="w-4 h-4 rounded-sm"
                      style={{ backgroundColor: intensity === 0 ? '#F1F5F9' : `rgba(15, 23, 42, ${0.15 + intensity * 0.85})` }}
                      title={`${WEEKDAY_LABELS_DOW[dow]} ${h}:00 — ${count} ejecuciones`}
                    />
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function MySalesView() {
  const { user } = useAuth();
  const paleta = usePaletaDeGraficas();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/tenant/reports/dashboard/mis-asignaciones?days=30');
      setData(res.data);
    } catch (err) {
      toast.error(formatApiError(err, 'No se pudieron cargar tus asignaciones'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading && !data) {
    return (
      <div className="flex justify-center py-20">
        <div className="w-8 h-8 border-2 border-[#E2E8F0] border-t-[#0F172A] rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="tenant-dashboard-seller" className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#0F172A] tracking-tight">Mis Ventas</h1>
          <p className="text-sm text-[#64748B] mt-1">Bienvenido, {user?.name} — tus leads, oportunidades, tareas y citas</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="dashboard-refresh">
          <RefreshCw className="w-3.5 h-3.5 mr-1" /> Actualizar
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard label="Leads activos" value={data?.leads.length ?? 0} />
        <KpiCard label="Oportunidades abiertas" value={data?.deals_open.length ?? 0} />
        <KpiCard label="Ventas ganadas (30 días)" value={data?.deals_won_count_period ?? 0} tone="text-emerald-700" />
        <KpiCard label="Tareas pendientes" value={data?.tasks_pending.length ?? 0} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Panel title="Mis leads" testId="panel-my-leads">
          {data?.leads.length ? (
            <div className="space-y-1.5">
              {data.leads.map(l => (
                <div key={l.id} className="flex items-center justify-between border-b border-[#F1F5F9] pb-1.5">
                  <span className="text-sm text-[#0F172A]">{l.display_name}</span>
                  <span className="text-xs text-[#94A3B8]">{l.phone || '—'}</span>
                </div>
              ))}
            </div>
          ) : <p className="text-sm text-[#94A3B8] text-center py-8">No tienes leads activos asignados</p>}
        </Panel>

        <Panel title="Mis oportunidades abiertas" testId="panel-my-deals">
          {data?.deals_open.length ? (
            <div className="space-y-1.5">
              {data.deals_open.map(d => (
                <div key={d.id} className="flex items-center justify-between border-b border-[#F1F5F9] pb-1.5">
                  <div>
                    <span className="text-sm text-[#0F172A]">{d.title}</span>
                    <span className="text-xs text-[#94A3B8] ml-2">{d.stage_key}</span>
                  </div>
                  <span className="text-xs font-medium text-[#0F172A]">{d.value_estimate != null ? money(d.value_estimate) : '—'}</span>
                </div>
              ))}
            </div>
          ) : <p className="text-sm text-[#94A3B8] text-center py-8">No tienes oportunidades abiertas</p>}
        </Panel>

        <Panel title="Mis tareas pendientes" testId="panel-my-tasks">
          {data?.tasks_pending.length ? (
            <div className="space-y-1.5">
              {data.tasks_pending.map(t => (
                <div key={t.id} className="flex items-center justify-between border-b border-[#F1F5F9] pb-1.5">
                  <span className="text-sm text-[#0F172A]">{t.title}</span>
                  <span className="text-xs text-[#94A3B8]">{t.due_date || 'sin fecha'}</span>
                </div>
              ))}
            </div>
          ) : <p className="text-sm text-[#94A3B8] text-center py-8">No tienes tareas pendientes</p>}
        </Panel>

        <Panel title="Mis próximas citas" testId="panel-my-appointments">
          {data?.appointments_upcoming.length ? (
            <div className="space-y-1.5">
              {data.appointments_upcoming.map(a => (
                <div key={a.id} className="flex items-center justify-between border-b border-[#F1F5F9] pb-1.5">
                  <span className="text-sm text-[#0F172A]">{a.starts_at ? new Date(a.starts_at).toLocaleString('es-MX') : '—'}</span>
                  <span className="text-xs text-[#94A3B8]">{a.status}</span>
                </div>
              ))}
            </div>
          ) : <p className="text-sm text-[#94A3B8] text-center py-8">No tienes citas próximas</p>}
        </Panel>
      </div>
    </div>
  );
}

// Sprint D — «Pregúntale a tu tablero»: caja de pregunta en lenguaje natural.
// Aislada: hace sus propias llamadas (sugeridas + pregunta) y NO depende de la
// carga pesada de FullDashboard. Si no hay IA conectada, el backend degrada con
// un aviso claro (nunca rompe la pantalla).
function AsistenteTablero({ diasDelRango }) {
  const [pregunta, setPregunta] = useState('');
  const [cargando, setCargando] = useState(false);
  const [respuesta, setRespuesta] = useState(null);
  const [aviso, setAviso] = useState('');
  const [sugerencias, setSugerencias] = useState([]);

  useEffect(() => {
    api.get('/tenant/reports/dashboard/preguntas-sugeridas')
      .then(r => setSugerencias(r.data?.sugerencias || []))
      .catch(() => {});
  }, []);

  const preguntar = async (texto) => {
    const q = (texto ?? pregunta).trim();
    if (!q || cargando) return;
    setPregunta(q);
    setCargando(true);
    setRespuesta(null);
    setAviso('');
    try {
      const r = await api.post('/tenant/reports/dashboard/pregunta', { pregunta: q, dias: diasDelRango || 30 });
      if (r.data?.answer) setRespuesta(r.data.answer);
      else setAviso((r.data?.warnings || [])[0] || 'No pude responder esta vez.');
    } catch (e) {
      setAviso(formatApiError(e));
    } finally {
      setCargando(false);
    }
  };

  return (
    <div className="bg-gradient-to-br from-[#0F172A] to-[#1E293B] rounded-xl p-4 sm:p-5 text-white" data-testid="dashboard-assistant">
      <div className="flex items-center gap-2 mb-1">
        <Sparkles className="w-4 h-4 text-amber-300" />
        <h2 className="text-base font-semibold">Pregúntale a tu tablero</h2>
      </div>
      <p className="text-xs text-white/70 mb-3">
        Escribe una pregunta en tus palabras (ej. «¿cómo vamos este mes?») y te respondo con los
        números reales de tu negocio. No inventa datos: si algo no está en tu tablero, te lo dice.
      </p>
      <div className="flex gap-2">
        <input
          type="text" value={pregunta} onChange={e => setPregunta(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') preguntar(); }}
          placeholder="¿Cómo vamos este mes?"
          className="flex-1 rounded-lg px-3 py-2 text-sm text-[#0F172A] bg-white placeholder:text-[#94A3B8]"
          data-testid="assistant-input" disabled={cargando}
        />
        <Button size="sm" onClick={() => preguntar()} disabled={cargando || !pregunta.trim()} data-testid="assistant-ask">
          <Send className="w-3.5 h-3.5 mr-1" /> {cargando ? 'Pensando…' : 'Preguntar'}
        </Button>
      </div>
      {sugerencias.length > 0 && !respuesta && !cargando && (
        <div className="flex flex-wrap gap-1.5 mt-3">
          {sugerencias.map((s, i) => (
            <button key={i} type="button" onClick={() => preguntar(s)}
              className="text-xs bg-white/10 hover:bg-white/20 rounded-full px-3 py-1 transition-colors"
              data-testid="assistant-suggestion">
              {s}
            </button>
          ))}
        </div>
      )}
      {cargando && <p className="text-sm text-white/70 mt-3">Revisando tus números…</p>}
      {aviso && !cargando && (
        <div className="mt-3 bg-amber-500/15 border border-amber-400/30 rounded-lg px-3 py-2 text-sm text-amber-100" data-testid="assistant-warning">
          {aviso}
        </div>
      )}
      {respuesta && !cargando && (
        <div className="mt-3 bg-white/10 rounded-lg px-4 py-3 text-sm whitespace-pre-wrap leading-relaxed" data-testid="assistant-answer">
          {respuesta}
        </div>
      )}
    </div>
  );
}

function FullDashboard() {
  const { user } = useAuth();
  const paleta = usePaletaDeGraficas();
  const [tab, setTab] = useState('resumen');
  const [days, setDays] = useState(30);
  // Abre en el mes en curso: un tablero que abre vacio obliga a configurar
  // antes de ver nada, y quien entra de paso no lo hace.
  const [filtros, setFiltros] = useState(() => mesActual());

  // El rango de fechas manda sobre el selector de periodo. Los paneles viejos
  // solo entienden "ultimos N dias", asi que el rango se traduce a dias para
  // que TODO el tablero mire la misma ventana. Sin esto, la barra cambiaria
  // dos graficas y dejaria las otras doce en otro periodo -- dos verdades en
  // la misma pantalla.
  const diasDelRango = (() => {
    if (!filtros.desde || !filtros.hasta) return days;
    const d = Math.ceil((new Date(filtros.hasta) - new Date(filtros.desde)) / 86400000) + 1;
    return Math.min(365, Math.max(1, d));
  })();
  const [loading, setLoading] = useState(true);

  const [directivo, setDirectivo] = useState(null);
  const [trend, setTrend] = useState(null);
  const [pipeline, setPipeline] = useState(null);
  const [marketing, setMarketing] = useState(null);
  const [iaAuto, setIaAuto] = useState(null);
  const [vertical, setVertical] = useState(null);

  const [campaigns, setCampaigns] = useState(null);
  const [porMes, setPorMes] = useState(null);
  const [porOrigen, setPorOrigen] = useState(null);
  const [referrals, setReferrals] = useState(null);
  const [sellers, setSellers] = useState(null);
  const [guards, setGuards] = useState(null);
  const [salesCross, setSalesCross] = useState(null);
  const [autoHeatmap, setAutoHeatmap] = useState(null);
  const [aiComparison, setAiComparison] = useState(null);
  const [caja, setCaja] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const paramsDeFiltro = new URLSearchParams(
      Object.entries({ vendedor: filtros.vendedor, campana: filtros.campana,
                       grupo: filtros.grupo, sucursal: filtros.sucursal })
        .filter(([, v]) => v)
    ).toString();
    try {
      // allSettled: cada panel es independiente. Un rol que no puede ver UN
      // panel (403) o un panel que falla no debe tumbar TODO el tablero —
      // antes con Promise.all un solo fallo dejaba el dashboard en blanco, y
      // por eso un admin/gerente lo veía "terrible" mientras el dueño lo veía
      // completo. El panel que no cargue simplemente no se muestra.
      const res = await Promise.allSettled([
        api.get(`/tenant/reports/dashboard/directivo?days=${diasDelRango}`),
        api.get(`/tenant/reports/dashboard/tendencia?days=${diasDelRango}`),
        api.get('/tenant/reports/dashboard/ventas'),
        api.get(`/tenant/reports/dashboard/marketing?days=${diasDelRango}`),
        api.get(`/tenant/reports/dashboard/ia-automatizacion?days=${diasDelRango}`),
        api.get(`/tenant/reports/dashboard/vertical?days=${diasDelRango}`),
        api.get(`/tenant/reports/dashboard/marketing/campanas?days=${diasDelRango}`),
        api.get(`/tenant/reports/dashboard/leads-por-mes?meses=6&${paramsDeFiltro}`),
        api.get(`/tenant/reports/dashboard/leads-por-origen?days=${diasDelRango}&${paramsDeFiltro}`),
        api.get('/tenant/reports/referrals'),
        api.get('/tenant/reports/sellers'),
        api.get('/tenant/reports/guards'),
        api.get(`/tenant/reports/dashboard/ventas/cruzado?days=${diasDelRango}`),
        api.get(`/tenant/reports/dashboard/automatizacion/mapa-calor?days=${diasDelRango}`),
        api.get(`/tenant/reports/dashboard/ia/comparativa?days=${diasDelRango}`),
        api.get(`/tenant/reports/dashboard/caja?days=${diasDelRango}`),
      ]);
      const val = (i) => (res[i].status === 'fulfilled' ? res[i].value.data : null);
      setDirectivo(val(0)); setTrend(val(1)); setPipeline(val(2)); setMarketing(val(3)); setIaAuto(val(4));
      setVertical(val(5)); setCampaigns(val(6));
      setPorMes(val(7)); setPorOrigen(val(8));
      setReferrals(val(9)); setSellers(val(10)); setGuards(val(11));
      setSalesCross(val(12)); setAutoHeatmap(val(13)); setAiComparison(val(14)); setCaja(val(15));
    } catch (err) {
      toast.error(formatApiError(err, 'No se pudieron cargar los paneles del dashboard'));
    } finally {
      setLoading(false);
    }
  }, [days, diasDelRango, filtros.vendedor, filtros.campana, filtros.grupo, filtros.sucursal]);

  useEffect(() => { load(); }, [load]);

  const exportCsv = async (endpoint, filename) => {
    try {
      const res = await api.get(endpoint, { responseType: 'blob' });
      downloadBlob(res.data, filename);
      toast.success('Archivo descargado');
    } catch (err) {
      toast.error(formatApiError(err, 'No se pudo exportar'));
    }
  };

  if (loading && !directivo) {
    return (
      <div className="flex justify-center py-20">
        <div className="w-8 h-8 border-2 border-[#E2E8F0] border-t-[#0F172A] rounded-full animate-spin" />
      </div>
    );
  }

  const closeRate = directivo?.pipeline?.close_rate_pct;
  const gaugeData = closeRate != null ? [{ name: 'Cierre', value: closeRate, fill: closeRate >= 50 ? '#10B981' : closeRate >= 25 ? '#F59E0B' : '#EF4444' }] : [];

  return (
    <div data-testid="tenant-dashboard" className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#0F172A] tracking-tight">Dashboard</h1>
          <p className="text-sm text-[#64748B] mt-1">Bienvenido, {user?.name} — todo tu negocio, en un solo lugar</p>
        </div>
        <div className="flex items-center gap-2">
          {/* Los atajos siguen ahí para quien solo quiere "los últimos 30
              días" sin pensar en fechas; al elegir uno se mueve el rango, que
              es lo que de verdad manda. Dos controles que dicen cosas
              distintas confunden más que uno solo. */}
          <select
            value={days}
            onChange={e => {
              const n = Number(e.target.value);
              setDays(n);
              const hoy = new Date();
              const desde = new Date(hoy.getTime() - (n - 1) * 86400000);
              const dos = x => String(x).padStart(2, '0');
              const iso = f => `${f.getFullYear()}-${dos(f.getMonth() + 1)}-${dos(f.getDate())}`;
              setFiltros(f => ({ ...f, desde: iso(desde), hasta: iso(hoy) }));
            }}
            className="text-sm border border-[#E2E8F0] rounded-lg px-3 py-1.5 bg-white" data-testid="dashboard-days-select">
            <option value={7}>Últimos 7 días</option>
            <option value={30}>Últimos 30 días</option>
            <option value={60}>Últimos 60 días</option>
            <option value={90}>Últimos 90 días</option>
          </select>
          <Button variant="outline" size="sm" onClick={load} data-testid="dashboard-refresh">
            <RefreshCw className="w-3.5 h-3.5 mr-1" /> Actualizar
          </Button>
        </div>
      </div>

      {/* La consulta del tablero. Todo lo de abajo obedece a esta barra. */}
      <div className="bg-white border border-[#E2E8F0] rounded-xl p-3">
        <FiltrosDelTablero filtros={filtros} onCambio={setFiltros} />
      </div>

      <AsistenteTablero diasDelRango={diasDelRango} />

      <div className="flex gap-1 border-b border-[#E2E8F0] overflow-x-auto" data-testid="dashboard-tabs">
        {TABS.map(t => (
          <button
            key={t.key} type="button" onClick={() => setTab(t.key)} data-testid={`dashboard-tab-${t.key}`}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
              tab === t.key ? 'border-[#0F172A] text-[#0F172A]' : 'border-transparent text-[#64748B] hover:text-[#0F172A]'
            }`}
          >
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      {tab === 'resumen' && directivo && (
        <div className="space-y-5" data-testid="tab-resumen">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <KpiCard label="Ingresos del periodo" value={money(directivo.revenue.total)} sub={`POS ${money(directivo.revenue.pos_total)} + Ventas ${money(directivo.revenue.deals_won_total)}`} />
            <KpiCard
              label="Leads nuevos" value={directivo.leads.this_period}
              tone={directivo.leads.change_pct > 0 ? 'text-emerald-700' : directivo.leads.change_pct < 0 ? 'text-red-700' : undefined}
              sub={directivo.leads.change_pct != null ? `${directivo.leads.change_pct > 0 ? '+' : ''}${directivo.leads.change_pct}% vs. periodo anterior` : 'Sin comparación disponible'}
            />
            <KpiCard label="Tasa de cierre" value={closeRate != null ? `${closeRate}%` : '—'} sub={`${directivo.pipeline.deals_won} ganadas · ${directivo.pipeline.deals_lost} perdidas`} />
            <KpiCard label="Alertas activas" value={directivo.alerts.length} tone={directivo.alerts.length ? 'text-amber-700' : 'text-emerald-700'} sub={directivo.alerts.length ? 'Revisa abajo' : 'Todo en orden'} />
          </div>

          {directivo.alerts.length > 0 && (
            <div className="space-y-2">
              {directivo.alerts.map((a, i) => (
                <div key={i} className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-4 py-2.5 text-sm text-amber-800">
                  <AlertTriangle className="w-4 h-4 shrink-0" /> {a.message}
                </div>
              ))}
            </div>
          )}

          {vertical?.is_developer && (
            <div className="space-y-4" data-testid="developer-sales-panel">
              <div className="flex items-end justify-between gap-2 flex-wrap">
                <div>
                  <h3 className="text-sm font-semibold text-[#0F172A]">Ventas de desarrollos</h3>
                  <p className="text-xs text-[#64748B]">Inventario real, embudo y puente con CRM — solo perfil Desarrollador.</p>
                </div>
              </div>
              {vertical?.kpis?.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3">
                  {vertical.kpis.map(k => (
                    <KpiCard key={k.label} label={k.label} value={k.value} sub={k.sub} />
                  ))}
                </div>
              )}
              {vertical?.developer?.by_development?.length > 0 && (
                <div className="bg-white border border-[#E2E8F0] rounded-xl overflow-hidden" data-testid="developer-by-dev-table">
                  <div className="px-4 py-2.5 border-b border-[#F1F5F9] text-xs font-semibold text-[#64748B] uppercase tracking-wide">Por desarrollo</div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-[11px] text-[#94A3B8] border-b border-[#F1F5F9]">
                          <th className="px-4 py-2 font-medium">Desarrollo</th>
                          <th className="px-3 py-2 font-medium">Libres</th>
                          <th className="px-3 py-2 font-medium">Apart.</th>
                          <th className="px-3 py-2 font-medium">Vend.</th>
                          <th className="px-3 py-2 font-medium">Absorción</th>
                          <th className="px-3 py-2 font-medium">$ libres</th>
                          <th className="px-3 py-2 font-medium">$ vendido lista</th>
                        </tr>
                      </thead>
                      <tbody>
                        {vertical.developer.by_development.map(d => (
                          <tr key={d.id} className="border-b border-[#F8FAFC] last:border-0">
                            <td className="px-4 py-2.5 font-medium text-[#0F172A]">{d.name}{d.city ? <span className="text-[#94A3B8] font-normal"> · {d.city}</span> : null}</td>
                            <td className="px-3 py-2.5 text-emerald-700">{d.counts?.available ?? 0}</td>
                            <td className="px-3 py-2.5 text-amber-700">{d.counts?.reserved ?? 0}</td>
                            <td className="px-3 py-2.5 text-slate-800">{d.counts?.sold ?? 0}</td>
                            <td className="px-3 py-2.5">{d.absorption_pct != null ? `${d.absorption_pct}%` : '—'}</td>
                            <td className="px-3 py-2.5 tabular-nums">{money(d.value_available)}</td>
                            <td className="px-3 py-2.5 tabular-nums">{money(d.value_sold)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {vertical?.developer?.funnel && (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2" data-testid="developer-funnel">
                  {[
                    ['Leads', vertical.developer.funnel.leads_period],
                    ['Visitas', vertical.developer.funnel.visits_period],
                    ['Apartados', vertical.developer.funnel.reservations_active],
                    ['Enganches', vertical.developer.funnel.plans_with_down_payment],
                    ['Unid. vendidas', vertical.developer.funnel.units_sold],
                    ['Deals won', vertical.developer.funnel.deals_won_period],
                  ].map(([label, val]) => (
                    <div key={label} className="rounded-lg border border-[#E2E8F0] bg-white px-3 py-2">
                      <p className="text-[10px] uppercase tracking-wide text-[#94A3B8]">{label}</p>
                      <p className="text-lg font-semibold text-[#0F172A] tabular-nums">{val ?? 0}</p>
                    </div>
                  ))}
                </div>
              )}
              {vertical?.developer?.bridge?.sold_without_crm_link > 0 && (
                <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="developer-bridge-warning">
                  {vertical.developer.bridge.sold_without_crm_link} unidad(es) marcada(s) vendida(s) en inventario sin deal ni plan de pagos ligado.
                  El ingreso del dashboard genérico (deals won) no las incluye — aquí sí cuentan en «Vendidas (inventario)».
                </div>
              )}
            </div>
          )}

          {!vertical?.is_developer && vertical?.kpis?.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {vertical.kpis.map(k => <KpiCard key={k.label} label={k.label} value={k.value} sub={k.sub} />)}
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2">
              <Panel title="Tendencia: leads e ingresos" help="Leads nuevos e ingresos reales día por día en el periodo elegido. Sin datos estimados — solo lo que ya pasó." testId="panel-trend">
                <div style={{ height: 260 }}>
                  {trend?.series?.length ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={trend.series}>
                        <defs>
                          <linearGradient id="gLeads" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#0F172A" stopOpacity={0.35} />
                            <stop offset="95%" stopColor="#0F172A" stopOpacity={0} />
                          </linearGradient>
                          <linearGradient id="gRevenue" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#10B981" stopOpacity={0.35} />
                            <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke={paleta.rejilla} />
                        <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={d => d.slice(5)} />
                        <YAxis yAxisId="leads" tick={{ fontSize: 11 }} allowDecimals={false} />
                        <YAxis yAxisId="revenue" orientation="right" tick={{ fontSize: 11 }} tickFormatter={v => `$${v}`} />
                        <RTooltip formatter={(v, name) => name === 'revenue' ? money(v) : v} labelFormatter={d => d} />
                        <Area yAxisId="leads" type="monotone" dataKey="leads" name="Leads" stroke={paleta.series[0]} fill="url(#gLeads)" strokeWidth={2} />
                        <Area yAxisId="revenue" type="monotone" dataKey="revenue" name="Ingresos" stroke={paleta.series[2]} fill="url(#gRevenue)" strokeWidth={2} />
                      </AreaChart>
                    </ResponsiveContainer>
                  ) : <p className="text-sm text-[#94A3B8] text-center py-16">Sin actividad registrada en el periodo</p>}
                </div>
              </Panel>
            </div>

            <Panel title="Tasa de cierre" help="Qué porcentaje de las oportunidades cerradas en el periodo se ganaron. Verde = 50%+, ámbar = 25-49%, rojo = menos de 25%." testId="panel-gauge">
              <div className="relative" style={{ height: 200 }}>
                {gaugeData.length ? (
                  <>
                    <ResponsiveContainer width="100%" height="100%">
                      <RadialBarChart innerRadius="70%" outerRadius="100%" data={gaugeData} startAngle={90} endAngle={-270}>
                        <PolarAngleAxis type="number" domain={[0, 100]} angleAxisId={0} tick={false} />
                        <RadialBar background dataKey="value" cornerRadius={8} angleAxisId={0} />
                      </RadialBarChart>
                    </ResponsiveContainer>
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <p className="text-3xl font-bold text-[#0F172A]">{closeRate}%</p>
                    </div>
                  </>
                ) : <p className="text-sm text-[#94A3B8] text-center py-16">Sin cierres en el periodo</p>}
              </div>
            </Panel>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {pipeline && (
              <Panel title="Pipeline por etapa" help="Oportunidades abiertas en cada etapa del embudo de ventas." testId="panel-pipeline">
                <div style={{ height: 220 }}>
                  {pipeline.by_stage.length ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={pipeline.by_stage}>
                        <CartesianGrid strokeDasharray="3 3" stroke={paleta.rejilla} />
                        <XAxis dataKey="stage_key" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                        <RTooltip />
                        <Bar dataKey="deal_count" name="Oportunidades" fill={paleta.series[1]} radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : <p className="text-sm text-[#94A3B8] text-center py-16">Sin oportunidades abiertas</p>}
                </div>
              </Panel>
            )}

            {marketing && (
              <Panel title="Fuente de leads nuevos" help="De dónde vienen tus leads (fuente de captura) en el periodo elegido." testId="panel-attribution">
                <div style={{ height: 220 }}>
                  {marketing.lead_attribution.length ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={marketing.lead_attribution} dataKey="count" nameKey="source_type" cx="50%" cy="50%" outerRadius={75} label={({ source_type }) => source_type}>
                          {marketing.lead_attribution.map((_, i) => <Cell key={i} fill={paleta.series[i % paleta.series.length]} />)}
                        </Pie>
                        <RTooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : <p className="text-sm text-[#94A3B8] text-center py-16">Sin leads en el periodo</p>}
                </div>
              </Panel>
            )}
          </div>
        </div>
      )}

      {tab === 'marketing' && marketing && campaigns && (
        <section className="space-y-5" data-testid="tab-marketing">
          {/* Cuando no hay envíos, esto ocupaba media pantalla con una tabla de
              siete columnas en blanco y un «sin actividad» que no explicaba
              nada. Medido el 18-08-2026: en toda la base hay CERO corridas de
              envío masivo, así que ese vacío es el estado normal de cualquier
              negocio que todavía no manda campañas — no una falla.
              Con datos se pinta la tabla igual que antes; sin datos, una nota
              chica que dice qué va a aparecer aquí y cómo llenarlo. */}
          <div>
            <h2 className="text-base font-semibold text-[#0F172A] flex items-center gap-2">
              Desempeño por campaña
              <HelpTooltip text="Por cada campaña que hayas enviado: cuántos mensajes salieron, cuántos prospectos trajo y cuántos de esos terminaron en venta. El prospecto se liga a la campaña por su nombre o su código, así que conviene usarlos igual en las dos partes." />
            </h2>
            {campaigns.campaigns.length ? (
              <div className="bg-white border border-[#E2E8F0] rounded-xl overflow-hidden mt-2">
                <table className="w-full text-sm">
                  <thead className="bg-[#F8FAFC] text-[#64748B] text-xs uppercase">
                    <tr>
                      <th className="text-left px-4 py-3 font-medium">Campaña</th>
                      <th className="text-right px-4 py-3 font-medium">Enviados</th>
                      <th className="text-right px-4 py-3 font-medium">Fallidos</th>
                      <th className="text-right px-4 py-3 font-medium">Prospectos</th>
                      <th className="text-right px-4 py-3 font-medium">Ventas</th>
                      <th className="text-right px-4 py-3 font-medium">Valor ganado</th>
                      <th className="text-right px-4 py-3 font-medium">Eficiencia</th>
                    </tr>
                  </thead>
                  <tbody>
                    {campaigns.campaigns.map(c => (
                      <tr key={c.campaign_id} className="border-t border-[#E2E8F0]">
                        <td className="px-4 py-3 font-medium text-[#0F172A]">{c.name}</td>
                        <td className="px-4 py-3 text-right">{c.sent}</td>
                        <td className="px-4 py-3 text-right text-red-600">{c.failed}</td>
                        <td className="px-4 py-3 text-right">{c.leads_generated}</td>
                        <td className="px-4 py-3 text-right text-emerald-700">{c.deals_won}</td>
                        <td className="px-4 py-3 text-right">{money(c.deals_won_value)}</td>
                        <td className="px-4 py-3 text-right font-medium">{c.efficiency_pct != null ? `${c.efficiency_pct}%` : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="bg-white border border-[#E2E8F0] rounded-xl px-4 py-3 mt-2 flex items-start gap-2" data-testid="campanas-sin-envios">
                <Megaphone className="w-4 h-4 text-[#94A3B8] mt-0.5 flex-shrink-0" />
                <p className="text-sm text-[#64748B]">
                  Todavía no has enviado ninguna campaña.{' '}
                  <span className="text-[#94A3B8]">
                    Cuando envíes una, aquí verás cuántos mensajes salieron, cuántos
                    prospectos trajo y cuántos terminaron en venta. Los prospectos que
                    ya te llegan por WhatsApp o redes se miden abajo, por canal.
                  </span>
                </p>
              </div>
            )}
          </div>

          {/* Prospectos por mes, con los canales dentro de cada barra.
              Los que no traen etiqueta se cuentan como «Sin identificar» en vez
              de tirarse: si se contaran solo los etiquetados, la grafica diria
              que el negocio recibe menos de lo que recibe, y un tablero que
              miente hacia abajo se ve bien y decide mal. */}
          {porMes && (
            <div className="bg-white border border-[#E2E8F0] rounded-xl p-4" data-testid="grafica-leads-por-mes">
              <div className="flex items-baseline justify-between mb-1">
                <p className="text-sm font-semibold text-[#0F172A] flex items-center gap-2">
                  Prospectos por mes
                  <HelpTooltip text="Cuántos prospectos entraron cada mes en los últimos 6 meses, y por qué canal llegó cada uno. «Sin identificar» son los que entraron sin dejar registro de su origen — normalmente altas manuales o importaciones." />
                </p>
                <span className="text-xs text-[#94A3B8]">{porMes.total} en 6 meses</span>
              </div>
              <div style={{ height: 260 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={porMes.serie} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F1F5F9" />
                    <XAxis dataKey="etiqueta" tick={{ fontSize: 11, fill: '#64748B' }} axisLine={false} tickLine={false} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#64748B' }} axisLine={false} tickLine={false} />
                    <RTooltip />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    {porMes.canales.map((canal, i) => (
                      <Bar key={canal} dataKey={canal} stackId="canales"
                           fill={paleta.series[i % paleta.series.length]} radius={i === porMes.canales.length - 1 ? [4, 4, 0, 0] : 0} />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Los mismos prospectos del periodo, cortados de tres formas. Suman
              igual a proposito: si un corte no cuadra con los otros, es que
              alguno esta perdiendo prospectos por el camino. */}
          {porOrigen && (
            <div data-testid="grafica-medio-canal-campana">
              <p className="text-sm font-semibold text-[#0F172A] flex items-center gap-2 mb-2">
                De dónde vienen — {porOrigen.total} prospectos en {porOrigen.dias} días
                <HelpTooltip text="Los mismos prospectos vistos de tres formas: por medio (el tipo de vía), por canal (por dónde entró) y por campaña u origen. Los tres suman lo mismo; si no cuadran, avísanos." />
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {[
                  { titulo: 'Por medio', datos: porOrigen.por_medio },
                  { titulo: 'Por canal', datos: porOrigen.por_canal },
                  { titulo: 'Por campaña u origen', datos: porOrigen.por_campana },
                ].map(({ titulo, datos }) => (
                  <div key={titulo} className="bg-white border border-[#E2E8F0] rounded-xl p-4">
                    <p className="text-xs text-[#94A3B8] mb-2">{titulo}</p>
                    {datos.length ? (
                      <ul className="space-y-2">
                        {datos.slice(0, 6).map((d, i) => (
                          <li key={d.nombre}>
                            <div className="flex items-baseline justify-between gap-2">
                              <span className="text-sm text-[#0F172A] truncate">{d.nombre}</span>
                              <span className="text-sm font-medium text-[#0F172A] flex-shrink-0">{d.cuantos}</span>
                            </div>
                            <div className="h-1.5 bg-[#F1F5F9] rounded mt-1 overflow-hidden">
                              <div className="h-full rounded"
                                   style={{ width: `${porOrigen.total ? Math.round(100 * d.cuantos / porOrigen.total) : 0}%`,
                                            background: paleta.series[i % paleta.series.length] }} />
                            </div>
                          </li>
                        ))}
                      </ul>
                    ) : <p className="text-sm text-[#94A3B8] py-6 text-center">Sin prospectos en el periodo</p>}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white border border-[#E2E8F0] rounded-xl p-4">
              <p className="text-xs text-[#94A3B8] mb-2 flex items-center gap-1">Envíos por canal <HelpTooltip text="Todos los envíos masivos del periodo, agrupados por canal (WhatsApp, email, etc.), sin importar de qué campaña vinieron." /></p>
              {marketing.by_channel.length ? (
                <table className="w-full text-sm">
                  <thead className="text-[#64748B] text-xs uppercase"><tr><th className="text-left py-1">Canal</th><th className="text-right py-1">Enviados</th><th className="text-right py-1">Fallidos</th></tr></thead>
                  <tbody>
                    {marketing.by_channel.map(c => (
                      <tr key={c.channel} className="border-t border-[#E2E8F0]"><td className="py-2">{c.channel}</td><td className="py-2 text-right">{c.sent}</td><td className="py-2 text-right text-red-600">{c.failed}</td></tr>
                    ))}
                  </tbody>
                </table>
              ) : <p className="text-sm text-[#94A3B8] text-center py-8">Sin envíos en el periodo</p>}
            </div>
            <div className="bg-white border border-[#E2E8F0] rounded-xl p-4" style={{ height: 220 }}>
              <p className="text-xs text-[#94A3B8] mb-2">Fuente de leads nuevos (envíos/recepción por canal)</p>
              {marketing.lead_attribution.length ? (
                <ResponsiveContainer width="100%" height="90%">
                  <PieChart>
                    <Pie data={marketing.lead_attribution} dataKey="count" nameKey="source_type" cx="50%" cy="50%" outerRadius={70} label={({ source_type }) => source_type}>
                      {marketing.lead_attribution.map((_, i) => <Cell key={i} fill={paleta.series[i % paleta.series.length]} />)}
                    </Pie>
                    <RTooltip />
                  </PieChart>
                </ResponsiveContainer>
              ) : <p className="text-sm text-[#94A3B8] text-center py-8">Sin leads en el periodo</p>}
            </div>
          </div>

          <div>
            <h2 className="text-base font-semibold text-[#0F172A] flex items-center gap-2">
              Experiencia del cliente (CEM)
              <HelpTooltip text="NPS, CSAT y CES del 1 al 5, de respuestas ya recibidas en automatizaciones." />
            </h2>
            {marketing.cem.nps && (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-2">
                <KpiCard label="NPS promedio" value={marketing.cem.nps.avg_score ?? '—'} sub={`${marketing.cem.nps.responses_count} respuestas`} />
                <KpiCard label="Promotores (4-5)" value={marketing.cem.nps.promoters} tone="text-emerald-700" />
                <KpiCard label="Pasivos (3)" value={marketing.cem.nps.passive} tone="text-amber-700" />
                <KpiCard label="Detractores (1-2)" value={marketing.cem.nps.detractors} tone="text-red-700" />
              </div>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between gap-3">
              <h2 className="text-base font-semibold text-[#0F172A]">Referidos</h2>
              <Button size="sm" variant="outline" onClick={() => exportCsv('/tenant/reports/referrals?format=csv', 'referidos.csv')}>
                <Download className="w-3.5 h-3.5 mr-1" /> Exportar CSV
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-3 max-w-md mt-2">
              <KpiCard label="Leads con código propio" value={referrals?.total_referrers ?? 0} />
              <KpiCard label="Leads referidos" value={referrals?.total_referred_leads ?? 0} />
            </div>
          </div>
        </section>
      )}

      {tab === 'ventas' && pipeline && salesCross && (
        <section className="space-y-5" data-testid="tab-ventas">
          <div>
            <h2 className="text-base font-semibold text-[#0F172A] flex items-center gap-2">
              Pipeline por etapa
              <HelpTooltip text="Oportunidades abiertas por etapa y su valor estimado." />
            </h2>
            <div className="bg-white border border-[#E2E8F0] rounded-xl p-4 mt-2" style={{ height: 240 }}>
              {pipeline.by_stage.length ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={pipeline.by_stage}>
                    <CartesianGrid strokeDasharray="3 3" stroke={paleta.rejilla} />
                    <XAxis dataKey="stage_key" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                    <RTooltip formatter={(v, name) => name === 'Valor' ? money(v) : v} />
                    <Bar dataKey="deal_count" name="Oportunidades" fill={paleta.series[0]} radius={[4, 4, 0, 0]} />
                    <Bar dataKey="value_estimate" name="Valor" fill={paleta.series[1]} radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : <p className="text-sm text-[#94A3B8] text-center py-16">Sin oportunidades abiertas</p>}
            </div>
          </div>

          <div>
            <h2 className="text-base font-semibold text-[#0F172A] flex items-center gap-2">
              Leads por vendedor por campaña
              <HelpTooltip text="Cruce entre Marketing y Ventas: cuántos leads de cada campaña recibió cada vendedor. Ayuda a ver si las campañas están repartiéndose parejo entre el equipo." />
            </h2>
            <div className="bg-white border border-[#E2E8F0] rounded-xl overflow-hidden mt-2">
              <table className="w-full text-sm">
                <thead className="bg-[#F8FAFC] text-[#64748B] text-xs uppercase">
                  <tr><th className="text-left px-4 py-3 font-medium">Vendedor</th><th className="text-left px-4 py-3 font-medium">Campaña</th><th className="text-right px-4 py-3 font-medium">Leads</th></tr>
                </thead>
                <tbody>
                  {salesCross.leads_by_seller_by_campaign.length ? salesCross.leads_by_seller_by_campaign.map((r, i) => (
                    <tr key={i} className="border-t border-[#E2E8F0]"><td className="px-4 py-3">{r.seller}</td><td className="px-4 py-3 text-[#64748B]">{r.campaign_name}</td><td className="px-4 py-3 text-right">{r.leads_count}</td></tr>
                  )) : (<tr><td colSpan={3} className="px-4 py-8 text-center text-[#94A3B8]">Sin leads con campaña asignados a un vendedor en el periodo</td></tr>)}
                </tbody>
              </table>
            </div>
          </div>

          {pipeline.loss_reasons.length > 0 && (
            <div>
              <h2 className="text-base font-semibold text-[#0F172A]">Motivos de pérdida</h2>
              <div className="bg-white border border-[#E2E8F0] rounded-xl overflow-hidden mt-2">
                <table className="w-full text-sm">
                  <thead className="bg-[#F8FAFC] text-[#64748B] text-xs uppercase"><tr><th className="text-left px-4 py-3 font-medium">Motivo</th><th className="text-right px-4 py-3 font-medium">Oportunidades</th></tr></thead>
                  <tbody>
                    {pipeline.loss_reasons.map(r => (
                      <tr key={r.reason} className="border-t border-[#E2E8F0]"><td className="px-4 py-3">{r.reason}</td><td className="px-4 py-3 text-right">{r.count}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div>
            <div className="flex items-center justify-between gap-3">
              <h2 className="text-base font-semibold text-[#0F172A]">Desempeño de vendedores</h2>
              <Button size="sm" variant="outline" onClick={() => exportCsv('/tenant/reports/sellers?format=csv', 'desempeno_vendedores.csv')}>
                <Download className="w-3.5 h-3.5 mr-1" /> Exportar CSV
              </Button>
            </div>
            <div className="bg-white border border-[#E2E8F0] rounded-xl overflow-hidden mt-2">
              <table className="w-full text-sm">
                <thead className="bg-[#F8FAFC] text-[#64748B] text-xs uppercase">
                  <tr>
                    <th className="text-left px-4 py-3 font-medium">Vendedor</th>
                    <th className="text-right px-4 py-3 font-medium">Leads asignados</th>
                    <th className="text-right px-4 py-3 font-medium">Ventas cerradas</th>
                    <th className="text-right px-4 py-3 font-medium">Respuesta (hrs)</th>
                  </tr>
                </thead>
                <tbody>
                  {(sellers?.rows || []).length ? sellers.rows.map(row => (
                    <tr key={row.user_id} className="border-t border-[#E2E8F0]">
                      <td className="px-4 py-3 font-medium text-[#0F172A]">{row.display_name}</td>
                      <td className="px-4 py-3 text-right">{row.leads_assigned}</td>
                      <td className="px-4 py-3 text-right">{row.deals_won}</td>
                      <td className="px-4 py-3 text-right text-[#64748B]">{row.avg_response_hours ?? '—'}</td>
                    </tr>
                  )) : (<tr><td colSpan={4} className="px-4 py-8 text-center text-[#94A3B8]">Sin asignaciones en el periodo</td></tr>)}
                </tbody>
              </table>
            </div>
          </div>

          <div>
            <h2 className="text-base font-semibold text-[#0F172A] flex items-center gap-2">
              Seguimientos y agendamientos por vendedor
              <HelpTooltip text="Tareas de seguimiento creadas y citas agendadas, por vendedor, en el periodo — mide actividad comercial real, no solo resultados." />
            </h2>
            <div className="bg-white border border-[#E2E8F0] rounded-xl overflow-hidden mt-2">
              <table className="w-full text-sm">
                <thead className="bg-[#F8FAFC] text-[#64748B] text-xs uppercase">
                  <tr><th className="text-left px-4 py-3 font-medium">Vendedor</th><th className="text-right px-4 py-3 font-medium">Tareas de seguimiento</th><th className="text-right px-4 py-3 font-medium">Citas agendadas</th></tr>
                </thead>
                <tbody>
                  {salesCross.seller_activity.length ? salesCross.seller_activity.map((r, i) => (
                    <tr key={i} className="border-t border-[#E2E8F0]"><td className="px-4 py-3">{r.seller}</td><td className="px-4 py-3 text-right">{r.tasks_count}</td><td className="px-4 py-3 text-right">{r.appointments_count}</td></tr>
                  )) : (<tr><td colSpan={3} className="px-4 py-8 text-center text-[#94A3B8]">Sin actividad registrada en el periodo</td></tr>)}
                </tbody>
              </table>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between gap-3">
              <h2 className="text-base font-semibold text-[#0F172A]">Cobertura de guardias</h2>
              <Button size="sm" variant="outline" onClick={() => exportCsv('/tenant/reports/guards?format=csv', 'reporte_guardias.csv')}>
                <Download className="w-3.5 h-3.5 mr-1" /> Exportar CSV
              </Button>
            </div>
            {guards?.summary && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-2">
                {[
                  { label: 'Turnos activos', value: guards.summary.active_shifts },
                  { label: 'Ausencias registradas', value: guards.summary.absence_records },
                  { label: 'Días de ausencia', value: guards.summary.absence_days_total },
                  { label: 'Guardias con ausencia', value: guards.summary.users_with_absence },
                ].map(k => <KpiCard key={k.label} label={k.label} value={k.value} />)}
              </div>
            )}
          </div>
        </section>
      )}

      {tab === 'automatizacion' && autoHeatmap && iaAuto && (
        <section className="space-y-5" data-testid="tab-automatizacion">
          <div>
            <h2 className="text-base font-semibold text-[#0F172A] flex items-center gap-2">
              Mapa de calor de ejecuciones (Trigger Engine)
              <HelpTooltip text="Cuándo se disparan tus automatizaciones — día de la semana × hora del día. Más oscuro = más ejecuciones. Útil para ver si tus automatizaciones corren cuando tu equipo puede reaccionar." />
            </h2>
            <div className="bg-white border border-[#E2E8F0] rounded-xl p-4 mt-2">
              {autoHeatmap.heatmap.length ? <Heatmap data={autoHeatmap.heatmap} /> : <p className="text-sm text-[#94A3B8] text-center py-8">Sin ejecuciones de disparadores en el periodo</p>}
            </div>
          </div>

          <div>
            <h2 className="text-base font-semibold text-[#0F172A] flex items-center gap-2">
              Embudo por nodo (Constructor de Flujos)
              <HelpTooltip text="Cada nodo de tus flujos: cuántas veces se ejecutó, cuántas falló, y cuánto tardó en promedio. Te dice exactamente dónde se atoran o fallan tus automatizaciones." />
            </h2>
            <div className="bg-white border border-[#E2E8F0] rounded-xl overflow-hidden mt-2">
              <table className="w-full text-sm">
                <thead className="bg-[#F8FAFC] text-[#64748B] text-xs uppercase">
                  <tr>
                    <th className="text-left px-4 py-3 font-medium">Flujo</th>
                    <th className="text-left px-4 py-3 font-medium">Nodo</th>
                    <th className="text-right px-4 py-3 font-medium">Ejecuciones</th>
                    <th className="text-right px-4 py-3 font-medium">Fallidas</th>
                    <th className="text-right px-4 py-3 font-medium">Duración promedio</th>
                  </tr>
                </thead>
                <tbody>
                  {autoHeatmap.node_funnel.length ? autoHeatmap.node_funnel.map((n, i) => (
                    <tr key={i} className="border-t border-[#E2E8F0]">
                      <td className="px-4 py-3 font-medium">{n.flow_name}</td>
                      <td className="px-4 py-3 text-[#64748B]">{n.node_type}</td>
                      <td className="px-4 py-3 text-right">{n.run_count}</td>
                      <td className="px-4 py-3 text-right text-red-600">{n.failed_count}</td>
                      <td className="px-4 py-3 text-right">{n.avg_duration_sec != null ? `${n.avg_duration_sec}s` : '—'}</td>
                    </tr>
                  )) : (<tr><td colSpan={5} className="px-4 py-8 text-center text-[#94A3B8]">Sin ejecuciones de Flow Builder en el periodo</td></tr>)}
                </tbody>
              </table>
            </div>
          </div>

          <div>
            <h2 className="text-base font-semibold text-[#0F172A]">Ejecuciones de automatizaciones de marketing</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-2">
              {iaAuto.automation_runs.length ? iaAuto.automation_runs.map(r => (
                <KpiCard key={r.status} label={`Estado: ${r.status}`} value={r.count} tone={r.status === 'completed' ? 'text-emerald-700' : r.status === 'failed' ? 'text-red-700' : undefined} />
              )) : <p className="text-sm text-[#94A3B8] col-span-full">Sin corridas en el periodo</p>}
            </div>
          </div>

          {iaAuto.trigger_actions.length > 0 && (
            <div>
              <h2 className="text-base font-semibold text-[#0F172A]">Acciones de disparadores (recordatorios, notificaciones)</h2>
              <div className="bg-white border border-[#E2E8F0] rounded-xl overflow-hidden mt-2">
                <table className="w-full text-sm">
                  <thead className="bg-[#F8FAFC] text-[#64748B] text-xs uppercase">
                    <tr><th className="text-left px-4 py-3 font-medium">Acción</th><th className="text-right px-4 py-3 font-medium">Éxito</th><th className="text-right px-4 py-3 font-medium">Fallidas</th><th className="text-right px-4 py-3 font-medium">Omitidas</th></tr>
                  </thead>
                  <tbody>
                    {iaAuto.trigger_actions.map(a => (
                      <tr key={a.action_type} className="border-t border-[#E2E8F0]">
                        <td className="px-4 py-3">{a.action_type}</td>
                        <td className="px-4 py-3 text-right text-emerald-700">{a.success}</td>
                        <td className="px-4 py-3 text-right text-red-700">{a.failed}</td>
                        <td className="px-4 py-3 text-right text-[#94A3B8]">{a.skipped}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </section>
      )}

      {tab === 'ia' && iaAuto && aiComparison && (
        <section className="space-y-5" data-testid="tab-ia">
          <div>
            <h2 className="text-base font-semibold text-[#0F172A] flex items-center gap-2">
              IA vs. Vendedores humanos
              <HelpTooltip text="Comparación real: cuántos mensajes salientes envió cada uno, qué tan rápido respondieron, y qué tan bien convirtieron los leads que atendieron mayoritariamente. Un lead es 'IA-mayoritario' si más de la mitad de sus respuestas fueron de la IA." />
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-2">
              <KpiCard label="Mensajes enviados por IA" value={aiComparison.volume.ai} />
              <KpiCard label="Mensajes enviados por humanos" value={aiComparison.volume.agent} />
              <KpiCard label="Respuesta promedio IA" value={aiComparison.avg_response_minutes.ai != null ? `${aiComparison.avg_response_minutes.ai} min` : '—'} />
              <KpiCard label="Respuesta promedio humano" value={aiComparison.avg_response_minutes.human != null ? `${aiComparison.avg_response_minutes.human} min` : '—'} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
              <div className="bg-white border border-[#E2E8F0] rounded-xl p-4">
                <p className="text-xs text-[#94A3B8]">Leads IA-mayoritarios ({aiComparison.conversion_cohorts.ai_majority.lead_count})</p>
                <p className="text-2xl font-bold text-[#0F172A]">{aiComparison.conversion_cohorts.ai_majority.won_rate_pct != null ? `${aiComparison.conversion_cohorts.ai_majority.won_rate_pct}%` : '—'}</p>
                <p className="text-xs text-[#64748B] mt-1">tasa de conversión a venta</p>
              </div>
              <div className="bg-white border border-[#E2E8F0] rounded-xl p-4">
                <p className="text-xs text-[#94A3B8]">Leads humano-mayoritarios ({aiComparison.conversion_cohorts.human_majority.lead_count})</p>
                <p className="text-2xl font-bold text-[#0F172A]">{aiComparison.conversion_cohorts.human_majority.won_rate_pct != null ? `${aiComparison.conversion_cohorts.human_majority.won_rate_pct}%` : '—'}</p>
                <p className="text-xs text-[#64748B] mt-1">tasa de conversión a venta</p>
              </div>
            </div>
          </div>

          <div>
            <h2 className="text-base font-semibold text-[#0F172A]">Uso y costo de IA</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-2">
              <KpiCard label="Solicitudes" value={iaAuto.ai_usage.requests_count} />
              <KpiCard label="Tokens usados" value={new Intl.NumberFormat('es-MX').format(iaAuto.ai_usage.total_tokens)} />
              <KpiCard label="Costo del periodo" value={money(iaAuto.ai_usage.total_cost)} />
              <KpiCard label="Feedback del equipo" value={`${iaAuto.ai_feedback.up} 👍 / ${iaAuto.ai_feedback.down} 👎`} />
            </div>
            {iaAuto.ai_cost_by_module.length > 0 && (
              <div className="bg-white border border-[#E2E8F0] rounded-xl p-4 mt-3" style={{ height: 220 }}>
                <p className="text-xs text-[#94A3B8] mb-2">Costo por módulo</p>
                <ResponsiveContainer width="100%" height="90%">
                  <BarChart data={iaAuto.ai_cost_by_module} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke={paleta.rejilla} />
                    <XAxis type="number" tick={{ fontSize: 11 }} />
                    <YAxis type="category" dataKey="module" tick={{ fontSize: 11 }} width={90} />
                    <RTooltip formatter={v => money(v)} />
                    <Bar dataKey="total_cost" fill={paleta.series[5]} radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </section>
      )}

      {tab === 'caja' && caja && (
        <section className="space-y-5" data-testid="tab-caja">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white border border-[#E2E8F0] rounded-xl p-4">
              <p className="text-xs text-[#94A3B8] mb-2 flex items-center gap-1">
                Ingresos por tipo de producto/servicio
                <HelpTooltip text="Solo ingresos reales — no incluye costo ni utilidad, porque el catálogo aún no tiene costo por producto." />
              </p>
              {caja.by_product_type.length ? (
                <table className="w-full text-sm">
                  <thead className="text-[#64748B] text-xs uppercase"><tr><th className="text-left py-1">Tipo</th><th className="text-right py-1">Ingresos</th><th className="text-right py-1">Ventas</th></tr></thead>
                  <tbody>
                    {caja.by_product_type.map(p => (
                      <tr key={p.item_type} className="border-t border-[#E2E8F0]"><td className="py-2 capitalize">{p.item_type}</td><td className="py-2 text-right">{money(p.revenue)}</td><td className="py-2 text-right">{p.count}</td></tr>
                    ))}
                  </tbody>
                </table>
              ) : <p className="text-sm text-[#94A3B8] text-center py-8">Sin ventas en el periodo</p>}
            </div>
            <div className="bg-white border border-[#E2E8F0] rounded-xl p-4">
              <p className="text-xs text-[#94A3B8] mb-2">Ingresos por método de pago</p>
              {caja.by_payment_method.length ? (
                <table className="w-full text-sm">
                  <thead className="text-[#64748B] text-xs uppercase"><tr><th className="text-left py-1">Método</th><th className="text-right py-1">Ingresos</th><th className="text-right py-1">Ventas</th></tr></thead>
                  <tbody>
                    {caja.by_payment_method.map(p => (
                      <tr key={p.method} className="border-t border-[#E2E8F0]"><td className="py-2 capitalize">{p.method}</td><td className="py-2 text-right">{money(p.revenue)}</td><td className="py-2 text-right">{p.count}</td></tr>
                    ))}
                  </tbody>
                </table>
              ) : <p className="text-sm text-[#94A3B8] text-center py-8">Sin ventas en el periodo</p>}
            </div>
          </div>

          <div>
            <h2 className="text-base font-semibold text-[#0F172A] flex items-center gap-2">
              Historial de caja
              <HelpTooltip text="Últimas sesiones de caja: monto de apertura, cierre esperado vs. contado, y diferencia (sobrante/faltante real)." />
            </h2>
            <div className="bg-white border border-[#E2E8F0] rounded-xl overflow-hidden mt-2">
              <table className="w-full text-sm">
                <thead className="bg-[#F8FAFC] text-[#64748B] text-xs uppercase">
                  <tr>
                    <th className="text-left px-4 py-3 font-medium">Apertura</th>
                    <th className="text-left px-4 py-3 font-medium">Cierre</th>
                    <th className="text-right px-4 py-3 font-medium">Monto inicial</th>
                    <th className="text-right px-4 py-3 font-medium">Esperado</th>
                    <th className="text-right px-4 py-3 font-medium">Contado</th>
                    <th className="text-right px-4 py-3 font-medium">Diferencia</th>
                  </tr>
                </thead>
                <tbody>
                  {caja.cash_sessions.length ? caja.cash_sessions.map(s => (
                    <tr key={s.id} className="border-t border-[#E2E8F0]">
                      <td className="px-4 py-3 text-[#64748B]">{s.opened_at ? new Date(s.opened_at).toLocaleString('es-MX') : '—'}</td>
                      <td className="px-4 py-3 text-[#64748B]">{s.closed_at ? new Date(s.closed_at).toLocaleString('es-MX') : 'Abierta'}</td>
                      <td className="px-4 py-3 text-right">{money(s.opening_amount)}</td>
                      <td className="px-4 py-3 text-right">{s.closing_amount_expected != null ? money(s.closing_amount_expected) : '—'}</td>
                      <td className="px-4 py-3 text-right">{s.closing_amount_counted != null ? money(s.closing_amount_counted) : '—'}</td>
                      <td className={`px-4 py-3 text-right font-medium ${s.difference > 0 ? 'text-emerald-700' : s.difference < 0 ? 'text-red-700' : ''}`}>
                        {s.difference != null ? money(s.difference) : '—'}
                      </td>
                    </tr>
                  )) : (<tr><td colSpan={6} className="px-4 py-8 text-center text-[#94A3B8]">Sin sesiones de caja en el periodo</td></tr>)}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

export default function TenantDashboard() {
  const { user } = useAuth();
  const paleta = usePaletaDeGraficas();
  // Vendedores (tenant_user): solo sus propias asignaciones, nunca reportes
  // de negocio. Dueños/admins/gerentes: panel completo de 6 pestañas.
  // El cajero nunca llega aquí — TenantLayout lo redirige a Administración.
  if (user?.tenant_role === 'tenant_user') {
    return <MySalesView />;
  }
  return <FullDashboard />;
}
